import React, { useState, useEffect } from 'react';
import { Box, Alert, Slide, Snackbar } from '@mui/material';
import { WifiOff, Wifi } from '@mui/icons-material';
import networkMonitor from '../services/networkMonitor';

/**
 * NetworkStatusBanner Component
 * 
 * Displays a banner when network is offline/online
 * Auto-hides when connection is restored
 */
const NetworkStatusBanner = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleNetworkChange = (status) => {
      const online = status === 'online';
      
      if (!online) {
        // Going offline
        setIsOnline(false);
        setWasOffline(true);
        setShowReconnected(false);
      } else {
        // Coming back online
        setIsOnline(true);
        
        // Show reconnected message only if we were offline before
        if (wasOffline) {
          setShowReconnected(true);
          // Auto-hide after 3 seconds
          setTimeout(() => {
            setShowReconnected(false);
            setWasOffline(false);
          }, 3000);
        }
      }
    };

    networkMonitor.addListener(handleNetworkChange);

    return () => {
      networkMonitor.removeListener(handleNetworkChange);
    };
  }, [wasOffline]);

  return (
    <>
      {/* Offline Banner - Always visible when offline */}
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: '#f44336',
            color: 'white',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            animation: 'slideDown 0.3s ease-out'
          }}
        >
          <WifiOff fontSize="small" />
          <span style={{ fontWeight: 500 }}>Ingen nettverkstilkobling</span>
        </Box>
      )}

      {/* Reconnected Notification - Slides in and auto-hides */}
      <Snackbar
        open={showReconnected}
        autoHideDuration={3000}
        onClose={() => setShowReconnected(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'down' }}
        sx={{ top: '16px !important' }}
      >
        <Alert
          severity="success"
          icon={<Wifi />}
          sx={{
            width: '100%',
            alignItems: 'center',
            fontWeight: 500
          }}
        >
          Tilkoblet - Oppdaterer data...
        </Alert>
      </Snackbar>

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default NetworkStatusBanner;
